import React, { useContext, useState } from 'react';
import {
  CircularProgress,
  Container,
  Grid,
  Switch,
  Typography
} from '@mui/material';
import TopologyColumn from './topologyColumn';
import {
  getAmount,
  getHighestConnectionValue,
  JSONRecordData
} from '../Log/graph.util';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import { FormattedMessage } from 'react-intl';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileEnergyHub, { HubRole } from './MobileEnergyHub';

interface TopologyProps {
  values: JSONRecordData;
  connected: boolean;
  loading: boolean;
}

function Topology(props: TopologyProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }
  const highestConnectionValue =
    props.values != null ? getHighestConnectionValue(props.values) : 0;

  const { product, setProduct } = useContext(ProductIdContext);

  const [showValues, setShowValues] = useState(false);

  const handleSwitch = () => () => {
    setShowValues(!showValues);
  };

  const isMobile = window.innerWidth <= 1490;
  // Phones (<md): let the horizontal one-line diagram scroll instead of
  // clipping both ends. Desktop (>=md) keeps the centered, non-scrolling layout.
  const isPhone = useIsMobile();

  const totalPvPower = props.values?.PvOnDc
    ? Object.values(props.values.PvOnDc).reduce(
        (sum, device) => sum + (device?.Dc?.Power || 0),
        0
      )
    : 0;

  // Phone view for SodistoreMax only. This file is shared with Salimax(0) and
  // Salidomo(1), which keep the full schematic on every screen size, so the hub is
  // behind an explicit allow-list rather than a "not Salimax" check.
  const HUB_PRODUCTS = new Set([3]);
  const showHub = isPhone && HUB_PRODUCTS.has(product ?? -1);

  const v: any = props.values;
  // Every box in the diagram below is gated on its device not being 'Disabled'.
  // The hub sums those same meters, so it has to honour the same gate — otherwise a
  // disabled meter's reading would be counted on mobile but not on desktop, and the
  // two views would report different totals for the same installation.
  const deviceOn = (key: string) =>
    v?.Config?.Devices?.[key]?.DeviceState !== 'Disabled';
  const term = (key: string, value: number | undefined) =>
    deviceOn(key) ? value ?? 0 : 0;

  const sumPv =
    term('PvOnDc', totalPvPower) +
    term('PvOnAcGrid', v?.PvOnAcGrid?.Power?.Active) +
    term('PvOnAcIsland', v?.PvOnAcIsland?.Power?.Active);
  const sumLoads =
    term('LoadOnAcGrid', v?.LoadOnAcGrid?.Power?.Active) +
    term('IslandBusLoadMeterIp', v?.LoadOnAcIsland?.Ac?.Power?.Active) +
    term('LoadOnDc', v?.LoadOnDc?.Power);
  const hubRoles: HubRole[] = [
    { key: 'pv', power: sumPv },
    { key: 'grid', power: term('GridMeterIp', v?.GridMeter?.Ac?.Power?.Active) },
    {
      key: 'battery',
      power: term('BatteryIp', v?.Battery?.Power),
      soc: v?.Battery?.Soc ?? 0
    },
    { key: 'loads', power: sumLoads }
  ];

  return (
    <Container maxWidth="xl" style={{ backgroundColor: 'white' }}>
      <Grid container>
        {!props.connected && !props.loading && (
          <Container
            maxWidth="xl"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh'
            }}
          >
            <CircularProgress size={60} style={{ color: '#ffc04d' }} />
            <Typography
              variant="body2"
              style={{ color: 'black', fontWeight: 'bold' }}
              mt={2}
            >
              <FormattedMessage id="unableToCommunicate" defaultMessage="Unable to communicate with the installation" />
            </Typography>
            <Typography variant="body2" style={{ color: 'black' }}>
              <FormattedMessage id="pleaseWaitOrRefresh" defaultMessage="Please wait or refresh the page" />
            </Typography>
          </Container>
        )}

        {props.connected && (
          <>
            {/* The hub always shows values in its boxes, so the Display Values
                toggle (which only labels the desktop diagram's flow lines) does
                nothing there — hide it on the mobile hub. Desktop and the
                Salimax/Salidomo mobile diagram keep it. */}
            {!showHub && (
            <Grid
              item
              xs={12}
              md={12}
              style={{
                marginTop: '10px',
                height: '20px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'right',
                justifyContent: 'right'
              }}
            >
              <div>
                <Typography sx={{ marginTop: '5px', marginRight: '20px' }}>
                  Display Values
                </Typography>
                <Switch
                  edge="start"
                  color="secondary"
                  onChange={handleSwitch()}
                  sx={{
                    '& .MuiSwitch-thumb': {
                      backgroundColor: 'orange'
                    },
                    marginLeft: '20px'
                  }}
                />
              </div>
            </Grid>
            )}

            {showHub ? (
              <Grid item xs={12}>
                <MobileEnergyHub roles={hubRoles} />
              </Grid>
            ) : (
            <Grid
              item
              xs={12}
              md={12}
              style={{
                height: isPhone ? 'auto' : isMobile ? '550px' : '600px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                // Phone: wrap wide diagrams to more rows so everything is visible
                // without horizontal scrolling; desktop stays one line.
                flexWrap: isPhone ? 'wrap' : 'nowrap',
                rowGap: isPhone ? '14px' : undefined
              }}
            >
              <TopologyColumn
                centerBox={{
                  title: 'Grid',
                  data: props.values?.GridMeter
                    ? [
                        {
                          value: props.values.GridMeter.Ac.L1.Power.Active,
                          unit: 'W'
                        },
                        {
                          value: props.values.GridMeter.Ac.L2.Power.Active,
                          unit: 'W'
                        },
                        {
                          value: props.values.GridMeter.Ac.L3.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.GridMeterIp.DeviceState !=
                    'Disabled'
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.GridMeter
                    ? {
                        value: props.values.GridMeter.Ac.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.GridMeter
                    ? getAmount(
                        highestConnectionValue,
                        props.values.GridMeter.Ac.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={true}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                topBox={{
                  title: 'Pv Inverter',
                  data: props.values?.PvOnAcGrid
                    ? [
                        {
                          value: props.values.PvOnAcGrid.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.PvOnAcGrid.DeviceState !==
                    'Disabled'
                }}
                topConnection={{
                  orientation: 'vertical',
                  position: 'top',
                  data: props.values?.PvOnAcGrid
                    ? {
                        value: props.values.PvOnAcGrid.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.PvOnAcGrid
                    ? getAmount(
                        highestConnectionValue,
                        props.values.PvOnAcGrid.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                centerBox={{
                  title: 'Grid Bus',
                  data: props.values?.GridMeter
                    ? [
                        {
                          value: props.values.GridMeter.Ac.L1.Voltage,
                          unit: 'V'
                        },
                        {
                          value: props.values.GridMeter.Ac.L2.Voltage,
                          unit: 'V'
                        },
                        {
                          value: props.values.GridMeter.Ac.L3.Voltage,
                          unit: 'V'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',

                  data: props.values?.AcGridToAcIsland
                    ? {
                        value: props.values.AcGridToAcIsland.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.AcGridToAcIsland
                    ? getAmount(
                        highestConnectionValue,
                        props.values.AcGridToAcIsland.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                bottomBox={{
                  title: 'AC Loads',
                  data: props.values?.LoadOnAcGrid
                    ? [
                        {
                          value: props.values.LoadOnAcGrid.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.LoadOnAcGrid.DeviceState !=
                    'Disabled'
                }}
                bottomConnection={{
                  orientation: 'vertical',
                  position: 'bottom',
                  data: props.values?.LoadOnAcGrid
                    ? {
                        value: props.values.LoadOnAcGrid.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.LoadOnAcGrid
                    ? getAmount(
                        highestConnectionValue,
                        props.values.LoadOnAcGrid.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                topBox={{
                  title: 'Pv Inverter',
                  data: props.values?.PvOnAcIsland
                    ? [
                        {
                          value: props.values.PvOnAcIsland.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.PvOnAcIsland.DeviceState !==
                    'Disabled'
                }}
                topConnection={{
                  orientation: 'vertical',
                  position: 'top',
                  data: props.values?.PvOnAcIsland
                    ? {
                        value: props.values.PvOnAcIsland.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.PvOnAcIsland
                    ? getAmount(
                        highestConnectionValue,
                        props.values.PvOnAcIsland.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                centerBox={{
                  title: 'Island Bus',
                  data: props.values?.AcDc
                    ? [
                        {
                          value: props.values.AcDc.Ac.L1.Voltage,
                          unit: 'V'
                        },
                        {
                          value: props.values.AcDc.Ac.L2.Voltage,
                          unit: 'V'
                        },
                        {
                          value: props.values.AcDc.Ac.L3.Voltage,
                          unit: 'V'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.AcDc
                    ? {
                        value: props.values.AcDc.Dc.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values.AcDc
                    ? getAmount(
                        highestConnectionValue,
                        props.values.AcDc.Dc.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                bottomBox={{
                  title: 'AC Loads',
                  data: props.values?.LoadOnAcIsland
                    ? [
                        {
                          value: props.values.LoadOnAcIsland.Ac.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.IslandBusLoadMeterIp
                      .DeviceState != 'Disabled'
                }}
                bottomConnection={{
                  orientation: 'vertical',
                  position: 'bottom',
                  data: props.values?.LoadOnAcIsland
                    ? {
                        value: props.values.LoadOnAcIsland.Ac.Power.Active,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.LoadOnAcIsland
                    ? getAmount(
                        highestConnectionValue,
                        props.values.LoadOnAcIsland.Ac.Power.Active
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                centerBox={{
                  title: 'AC-DC',
                  data: props.values.AcDc
                    ? [
                        {
                          value: props.values.AcDc.Ac.L1.Power.Active,
                          unit: 'W'
                        },
                        {
                          value: props.values.AcDc.Ac.L2.Power.Active,
                          unit: 'W'
                        },
                        {
                          value: props.values.AcDc.Ac.L3.Power.Active,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.AcDcToDcLink
                    ? {
                        value: props.values.AcDcToDcLink.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.AcDcToDcLink
                    ? getAmount(
                        highestConnectionValue,
                        props.values.AcDcToDcLink.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}
              <TopologyColumn
                topBox={{
                  title: 'Pv DC-DC',
                  data: props.values?.PvOnDc
                    ? [
                        {
                          value: totalPvPower,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.PvOnDc.DeviceState != 'Disabled'
                }}
                topConnection={{
                  orientation: 'vertical',
                  position: 'top',
                  data: props.values?.PvOnDc
                    ? {
                        value: totalPvPower,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.PvOnDc
                    ? getAmount(highestConnectionValue, totalPvPower)
                    : 0,
                  showValues: showValues
                }}
                centerBox={{
                  title: 'DC Link',
                  data: props.values?.DcDc
                    ? [
                        {
                          value: props.values.DcDc.Dc.Link.Voltage,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: props.values?.DcDc
                    ? {
                        value: props.values.DcDc.Dc.Link.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.DcDc
                    ? getAmount(
                        highestConnectionValue,
                        props.values.DcDc.Dc.Link.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                bottomBox={{
                  title: 'DC Loads',
                  data: props.values?.LoadOnDc
                    ? [
                        {
                          value: props.values.LoadOnDc.Power,
                          unit: 'W'
                        }
                      ]
                    : undefined,
                  connected:
                    props.values.Config.Devices.LoadOnDc.DeviceState !=
                    'Disabled'
                }}
                bottomConnection={{
                  orientation: 'vertical',
                  position: 'bottom',
                  data: props.values?.LoadOnDc
                    ? {
                        value: props.values.LoadOnDc.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.LoadOnDc
                    ? getAmount(
                        highestConnectionValue,
                        props.values.LoadOnDc.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              {/*-------------------------------------------------------------------------------------------------------------------------------------------------------------*/}

              <TopologyColumn
                centerBox={{
                  title: 'DC-DC',
                  data: props.values?.DcDc
                    ? [
                        {
                          value: props.values.DcDc.Dc.Battery.Voltage,
                          unit: 'V'
                        }
                      ]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',

                  data: props.values?.Battery
                    ? {
                        //value: props.values.Battery.Dc.Power for salimax,
                        value:
                          product == 0
                            ? props.values.Battery.Dc.Power
                            : props.values.Battery.Power,
                        unit: 'W'
                      }
                    : undefined,
                  amount: props.values?.Battery
                    ? getAmount(
                        highestConnectionValue,
                        product == 0
                          ? props.values.Battery.Dc.Power
                          : props.values.Battery.Power
                      )
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />
              <TopologyColumn
                centerBox={{
                  title: 'Battery',
                  data: props.values.Battery
                    ? [
                        {
                          value: props.values.Battery.Soc,
                          unit: '%'
                        },
                        {
                          //value: props.values.Battery.Dc.Voltage for salimax,
                          value:
                            product === 0
                              ? props.values.Battery.Dc.Voltage
                              : props.values.Battery.Voltage,
                          unit: 'V'
                        },
                        {
                          //value: props.values.Battery.Dc.Current for salimax,

                          value:
                            product == 0
                              ? props.values.Battery.Dc.Current
                              : props.values.Battery.Current,
                          unit: 'A'
                        },
                        {
                          //value: props.values.Battery.Temperature for salimax,
                          value:
                            product == 0
                              ? props.values.Battery.Temperature
                              : props.values.Battery.TemperatureCell1,
                          unit: '°C'
                        }
                        // {
                        //   value: Object.keys(props.values.Battery.Devices)
                        //     .length,
                        //   unit: ''
                        // }
                      ]
                    : undefined,

                  connected:
                    props.values.Config.Devices.BatteryIp.DeviceState !=
                    'Disabled'
                }}
                isLast={true}
                isFirst={false}
              />
            </Grid>
            )}
          </>
        )}
      </Grid>
    </Container>
  );
}

export default Topology;
